import localforage from 'localforage';

// 配置localforage
localforage.config({
      name: 'buyer-web', // 应用名称
  storeName: 'keyValue', // 存储名称
  description: 'Seller Web Application Storage' // 描述
});

// 检测存储可用性
const isStorageAvailable = (): boolean => {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

// 获取当前使用的存储驱动
const getCurrentDriver = (): string => {
  return localforage.driver() || 'unknown';
};

export const Storage = {
  /**
   * 获取存储项
   * @param key 键名
   * @returns 存储的值，如果不存在返回null
   */
  getItem: async (key: string): Promise<string | null> => {
    try {
      const value = await localforage.getItem(key);
      return value !== null ? String(value) : null;
    } catch (error) {
      console.error('Storage getItem error:', error);
      // 降级到localStorage
      if (isStorageAvailable()) {
        try {
          return localStorage.getItem(key);
        } catch (e) {
          console.error('localStorage fallback error:', e);
        }
      }
      return null;
    }
  },

  /**
   * 设置存储项
   * @param key 键名
   * @param value 要存储的值
   */
  setItem: async (key: string, value: string): Promise<void> => {
    try {
      await localforage.setItem(key, value);
    } catch (error) {
      console.error('Storage setItem error:', error);
      // 降级到localStorage
      if (isStorageAvailable()) {
        try {
          localStorage.setItem(key, value);
        } catch (e) {
          console.error('localStorage fallback error:', e);
        }
      }
    }
  },

  /**
   * 删除存储项
   * @param key 键名
   */
  removeItem: async (key: string): Promise<void> => {
    try {
      await localforage.removeItem(key);
    } catch (error) {
      console.error('Storage removeItem error:', error);
      // 降级到localStorage
      if (isStorageAvailable()) {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.error('localStorage fallback error:', e);
        }
      }
    }
  },

  /**
   * 清空所有存储
   */
  clear: async (): Promise<void> => {
    try {
      await localforage.clear();
    } catch (error) {
      console.error('Storage clear error:', error);
      // 降级到localStorage
      if (isStorageAvailable()) {
        try {
          localStorage.clear();
        } catch (e) {
          console.error('localStorage fallback error:', e);
        }
      }
    }
  },

  /**
   * 获取所有键名
   * @returns 所有存储键的数组
   */
  keys: async (): Promise<string[]> => {
    try {
      return await localforage.keys();
    } catch (error) {
      console.error('Storage keys error:', error);
      // 降级到localStorage
      if (isStorageAvailable()) {
        try {
          return Object.keys(localStorage);
        } catch (e) {
          console.error('localStorage fallback error:', e);
        }
      }
      return [];
    }
  },

  /**
   * 获取存储信息
   * @returns 当前存储状态信息
   */
  getInfo: () => {
    return {
      driver: getCurrentDriver(),
      isAvailable: isStorageAvailable(),
      supportsIndexedDB: 'indexedDB' in window,
      supportsLocalStorage: isStorageAvailable()
    };
  }
};

// 初始化存储驱动
localforage.ready().then(() => {
  console.log('Storage initialized with driver:', getCurrentDriver());
}).catch((error) => {
  console.error('Storage initialization failed:', error);
}); 